// CARO. Casa de estilo — lightweight document head manager for the SPA.
// Sets title, meta description, canonical, hreflang alternates and OG tags per
// route without a helmet dependency. Locale-aware: canonical/og:url/hreflang
// reflect the active language (ES at root, EN under /en, FR under /fr). The
// Worker injects the same tags server-side for no-JS crawlers
// (worker/lib/pageMeta.ts); this keeps them correct across client-side navigations.

import { useEffect } from 'react';
import { LOCALES, DEFAULT_LOCALE } from '@/config/site';
import {
  resolveMeta,
  canonicalUrl,
  localizedPath,
  OG_LOCALE,
  type MetaInput,
} from '@/lib/seo';
import { useI18n } from '@/client/i18n/I18nContext';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Replace every `<meta property="…">` of a multi-valued key with `contents`. */
function replaceMetaList(property: string, contents: string[]) {
  document.head
    .querySelectorAll<HTMLMetaElement>(`meta[property="${property}"]`)
    .forEach((el) => el.remove());
  for (const content of contents) {
    const el = document.createElement('meta');
    el.setAttribute('property', property);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertAlternate(hreflang: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`,
  );
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useMeta(input: MetaInput): void {
  const { locale } = useI18n();
  const localePathsKey = input.localePaths ? JSON.stringify(input.localePaths) : '';
  useEffect(() => {
    const meta = resolveMeta(input, locale);
    const path = input.path ?? '/';

    document.title = meta.title;
    document.documentElement.lang = locale;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.ogTitle);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:type', meta.type);
    upsertMeta('property', 'og:url', meta.canonical);
    if (meta.ogImage) {
      upsertMeta('property', 'og:image', meta.ogImage);
      upsertMeta('property', 'og:image:alt', meta.ogTitle);
    }
    upsertMeta('property', 'og:locale', OG_LOCALE[locale]);
    replaceMetaList(
      'og:locale:alternate',
      LOCALES.filter((l) => l !== locale).map((l) => OG_LOCALE[l]),
    );
    // Same values as the Worker (pageMeta): noindex pages still pass link signals.
    upsertMeta('name', 'robots', meta.noindex ? 'noindex,follow' : 'index,follow');
    upsertLink('canonical', meta.canonical);
    // hreflang only on indexable pages, like the Worker.
    if (meta.noindex) {
      document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    } else {
      const pathIn = (l: typeof locale) => input.localePaths?.[l] ?? path;
      for (const l of LOCALES) upsertAlternate(l, canonicalUrl(localizedPath(l, pathIn(l))));
      upsertAlternate('x-default', canonicalUrl(localizedPath(DEFAULT_LOCALE, pathIn(DEFAULT_LOCALE))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.title, input.ogTitle, input.description, input.path, input.image, input.type, input.noindex, localePathsKey, locale]);
}
